import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import { find, get, isEmpty } from 'lodash';
import {
  intlTaxTypes
} from '../../../data/translationsData';

import { BookingDetailsSalesTax } from './tables/BookingDetailsSalesTax';
import { SpaceRentalSalesTaxTable } from './tables/SpaceRentalSalesTaxTable';
import { PriceSalesTaxTable } from './tables/PriceSalesTaxTable';
import { AddonsSalesTaxTable } from './tables/AddonsTables/AddonsSalesTaxTable';
import { IncludedItemsSalesTaxTable } from './tables/AddonsTables/IncludedItemsSalesTaxTable';
import { InvoiceCountryTaxDetails } from '../../../../shared-components/invoice/InvoiceCountryTaxDetails';

export const SalesTaxInvoice = ({
  invoiceId,
  date,
  invoiceCountry,
  invoiceFeeName,
  guestDetails,
  spaceRentalEntry,
  addonsEntry,
  taxNumberName,
  taxNumber,
  contactPhoneNumber,
  contracts
}) => {
  const spaceRentalContract = find(contracts, (c) => 'SPACE_RENTAL' === c.type) || {};

  const schedule = get(spaceRentalContract, 'meta.booking.schedule', null);
  const timeZoneId = get(spaceRentalContract, 'meta.booking.timezone.id', 'America/Los_Angeles');
  const useType = get(spaceRentalContract, 'meta.listing.use_type', '');
  const spaceAddress = { ...get(spaceRentalContract, 'meta.booking.space.address_parts', {}), country: invoiceCountry };

  const currency = get(spaceRentalContract, 'meta.listing.prices[0].currency', '');

  const spaceRental = get(spaceRentalEntry, 'guest_space_rental', 0);
  const spaceRentalUsSalesTax = get(spaceRentalEntry, 'guest_us_space_rental_tax', 0);
  const spaceRentalTotal = get(spaceRentalEntry, 'guest_space_rental_with_tax', 0);

  const addOnsTotal = get(addonsEntry, 'guest_add_ons', 0);
  const usAddOnTaxTotal = get(addonsEntry, 'guest_us_add_on_tax', 0);
  const addOnsWithTaxTotal = get(addonsEntry, 'guest_add_ons_with_tax', 0);
  const addOnsItems = get(addonsEntry, 'guest_us_add_on_items', []);

  const includedItemsTotal = get(addonsEntry, 'guest_included_items', 0);
  const includedItemsTax = get(addonsEntry, 'guest_us_included_items_tax', 0);
  const includedItemsWithTaxTotal = get(addonsEntry, 'guest_included_items_with_tax', 0);
  const includedItems = get(addonsEntry, 'guest_us_included_items', []);

  const subtotalWithTax = get(spaceRentalEntry, 'guest_subtotal_with_tax', 0);
  const serviceFee = get(spaceRentalEntry, 'guest_platform_fee', 0);

  const parsedSubtotalWithTax = +subtotalWithTax;
  const parsedServiceFee = +serviceFee;

  const total = isEmpty(spaceRentalEntry)
    ? addOnsWithTaxTotal
    : isNaN(parsedSubtotalWithTax)
      ? parsedServiceFee : isNaN(parsedServiceFee)
        ? parsedSubtotalWithTax
        : parsedSubtotalWithTax + parsedServiceFee;

  return (
    <div id="invoice-pdf-page" style={{ width: '100vw' }}>
      <div className="flex space-between middle">
        <h1 className="">
          {date}
        </h1>
        <div id="peerspace-logo" className="none">
          <img
            style={{ width: 160 }}
            src="https://assets.peerspace.com/ps_logo_dark-v2.svg?v=rb"
            alt="peerspace"
          />
        </div>
      </div>

      <h4 className="text-gray-dark no-top-margin">
        {invoiceCountry}
        &nbsp;
        {intlTaxTypes[invoiceFeeName] && <span className="margin-sm-left margin-sm-right"><FormattedMessage {...intlTaxTypes[invoiceFeeName]} />&nbsp;</span>}
        <FormattedMessage id="hosting.invoice_page.invoice" defaultMessage="Invoice" />
      </h4>

      <div className="text-small text-gray-600 margin-xxl-top">
        <div>
          <span className="margin-sm-right"><FormattedMessage id="hosting.invoice_page.last_updated_text" defaultMessage="Last Updated" />:</span>
        </div>
        <div>{`Invoice #: ${invoiceId}`}</div>
      </div>

      <hr className="margin-lg-bottom margin-lg-top" />

      {((taxNumberName && taxNumber) || contactPhoneNumber) &&
        <>
          <InvoiceCountryTaxDetails
            taxName={taxNumberName}
            taxNumber={taxNumber}
            phoneNumber={contactPhoneNumber}
          />
          <hr className="margin-lg-bottom margin-lg-top" />
        </>
      }

      <div className="margin-xl-top">
        <BookingDetailsSalesTax
          guestDetails={guestDetails}
          address={spaceAddress}
          timeZoneId={timeZoneId}
          schedule={schedule}
          useType={useType}
        />
      </div>

      {!isEmpty(spaceRentalEntry)
        && <div className="margin-xl-top">
          <SpaceRentalSalesTaxTable
            spaceRental={spaceRental}
            spaceRentalUsSalesTax={spaceRentalUsSalesTax}
            spaceRentalTotal={spaceRentalTotal}
            currency={currency}
          />
        </div>
      }

      {!isEmpty(addOnsItems)
        && <div className="margin-xl-top">
          <AddonsSalesTaxTable
            total={addOnsTotal}
            taxTotal={usAddOnTaxTotal}
            totalWithTax={addOnsWithTaxTotal}
            items={addOnsItems}
            currency={currency}
          />
        </div>
      }

      {!isEmpty(includedItems)
        && <div className="margin-xl-top">
          <IncludedItemsSalesTaxTable
            total={includedItemsTotal}
            taxTotal={includedItemsTax}
            totalWithTax={includedItemsWithTaxTotal}
            items={includedItems}
            currency={currency}
          />
        </div>
      }

      <div className="margin-xl-top">
        <PriceSalesTaxTable
          addOnsWithTaxTotal={addOnsWithTaxTotal}
          spaceRentalTotal={spaceRentalTotal}
          subtotalWithTax={subtotalWithTax}
          includedItemsWithTax={includedItemsWithTaxTotal}
          serviceFee={serviceFee}
          total={total}
          currency={currency}
        />
      </div>
    </div>
  );
};

SalesTaxInvoice.propTypes = {
  invoiceId: PropTypes.string,
  date: PropTypes.string,
  invoiceCountry: PropTypes.string,
  invoiceFeeName: PropTypes.string,
  guestDetails: PropTypes.object,
  spaceRentalEntry: PropTypes.object,
  addonsEntry: PropTypes.object,
  contracts: PropTypes.array,
  taxNumberName: PropTypes.node,
  taxNumber: PropTypes.string,
  contactPhoneNumber: PropTypes.string,
  intl: intlShape
};
